import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

import {
  DescriptionRunResultDTO,
  ListDescriptionRunsStatusEnum,
  PlatformDescriptionStatusDTO
} from "@app/api/generated";
import { useUrlListState } from "@app/util/use-url-list-state";

import { getPlatformStatuses, listRuns, RUNS_PAGE_SIZE } from "@app/api/product-description-api";

const POLL_INTERVAL_MS = 4000;

interface IUseDescriptionDashboard {
  platforms: PlatformDescriptionStatusDTO[];
  runs: DescriptionRunResultDTO[];
  isLoading: boolean;
  currentPage: number;
  total: number;
  totalPages: number;
  statusFilter?: ListDescriptionRunsStatusEnum;
  platformFilter?: number;
  /** True while any platform is RUNNING — drives polling and the "generate all" disable. */
  anyRunning: boolean;
  refresh: () => Promise<void>;
  setPlatforms: Dispatch<SetStateAction<PlatformDescriptionStatusDTO[]>>;
  setCurrentPage: (page: number) => void;
  setStatusFilter: (status?: ListDescriptionRunsStatusEnum) => void;
  setPlatformFilter: (platformId?: number) => void;
}

const isValidStatus = (value: string | null): value is ListDescriptionRunsStatusEnum =>
  value !== null && (Object.values(ListDescriptionRunsStatusEnum) as string[]).includes(value);

/**
 * Loads the platform aggregate (sidebar) plus a filtered page of runs (table), and polls both while
 * anything is RUNNING. The DB is the source of truth for `isRunning`, so this stays correct even if
 * a run was triggered by the cron or elsewhere.
 *
 * Page and filters live in the URL query so the view survives a refresh.
 */
export const useDescriptionDashboard = (): IUseDescriptionDashboard => {
  const intl = useIntl();
  const { page: currentPage, filters, setPage, setFilters } = useUrlListState(["status", "platform"] as const);
  const [platforms, setPlatforms] = useState<PlatformDescriptionStatusDTO[]>([]);
  const [runs, setRuns] = useState<DescriptionRunResultDTO[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const statusParam = filters.status ?? null;
  const statusFilter = isValidStatus(statusParam) ? statusParam : undefined;
  const platformParam = Number(filters.platform);
  const platformFilter = Number.isInteger(platformParam) && platformParam > 0 ? platformParam : undefined;

  // Guards against setState after unmount when a fetch resolves late. Set on mount as well, so a
  // remount of the same instance (StrictMode runs the cleanup once in dev) does not freeze the view.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const [statuses, runsPage] = await Promise.all([
      getPlatformStatuses(),
      listRuns({ page: currentPage, platformId: platformFilter, status: statusFilter })
    ]);
    if (!mountedRef.current) {
      return;
    }
    setPlatforms(statuses);
    setRuns(runsPage.items);
    setTotal(runsPage.total);
  }, [currentPage, platformFilter, statusFilter]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    (async () => {
      try {
        await refresh();
      } catch {
        // The poll may swallow a hiccup because data is already on screen; the first load may not,
        // otherwise the dashboard stays empty without a word about why.
        if (mounted) {
          toast.error(intl.formatMessage({ id: "productDescription.toast.loadError" }));
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const anyRunning = platforms.some((platform) => platform.isRunning);

  useEffect(() => {
    if (!anyRunning) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      // Swallow transient poll errors — the next tick recovers.
      refresh().catch(() => undefined);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [anyRunning, refresh]);

  return {
    platforms,
    runs,
    isLoading,
    currentPage,
    total,
    totalPages: Math.ceil(total / RUNS_PAGE_SIZE),
    statusFilter,
    platformFilter,
    anyRunning,
    refresh,
    setPlatforms,
    setCurrentPage: setPage,
    setStatusFilter: (status?: ListDescriptionRunsStatusEnum) => setFilters({ status }),
    setPlatformFilter: (platformId?: number) => setFilters({ platform: platformId })
  };
};
